import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, GraduationCap, Cloud, CheckSquare, Code2 } from "lucide-react";
import { PRODUCTS, IMAGES } from "@/lib/data";
import BgLayer from "@/components/ui-shared/BgLayer";

const iconOrder = [Sparkles, GraduationCap, Cloud, CheckSquare, Code2];

const Products = () => (
    <div data-testid="products-page">
        <section className="relative pt-32 md:pt-40 pb-16 overflow-hidden">
            <BgLayer variant="mesh" intensity="strong" />
            <div className="sxe-container relative grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
                <div>
                    <div className="overline"><span className="dot" /> Products</div>
                    <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter leading-[1.02] text-white mt-6">
                        The <span className="text-gradient">SpringXE stack</span>
                    </h1>
                    <p className="text-[#B0A3C4] text-lg md:text-xl leading-relaxed mt-6 max-w-xl">
                        Five purpose-built products that operate as one AI-native platform — replacing a decade of stitched-together point tools with a single, coherent enterprise experience.
                    </p>
                </div>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="relative rounded-2xl overflow-hidden border border-[#C266FF]/20"
                >
                    <img src={IMAGES.dashboard} alt="SpringXE platform dashboard" className="w-full h-[340px] object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#07020F] via-transparent to-transparent" />
                </motion.div>
            </div>
        </section>

        <section className="pb-24 relative">
            <BgLayer variant="dots" />
            <div className="sxe-container relative grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {PRODUCTS.map((p, i) => {
                    const Icon = iconOrder[i] || Sparkles;
                    return (
                        <motion.div
                            key={p.slug}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.5, delay: i * 0.04 }}
                            className="sxe-card overflow-hidden group"
                            data-testid={`product-card-${p.slug}`}
                        >
                            <Link to={`/products/${p.slug}`}>
                                <div className="relative h-40 overflow-hidden">
                                    <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#0d0621] via-[#0d0621]/40 to-transparent" />
                                    <div className="absolute top-3 left-3 w-10 h-10 rounded-xl bg-gradient-to-br from-[#A100FF] to-[#7B00CC] flex items-center justify-center">
                                        <Icon className="w-5 h-5 text-white" />
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="font-mono text-[10px] text-[#75668C]">PRODUCT / 0{i + 1}</div>
                                    <h3 className="font-display text-white text-xl font-medium tracking-tight mt-2">{p.title}</h3>
                                    <div className="text-[#C266FF] text-[13px] font-display mt-1">{p.tagline}</div>
                                    <p className="text-[#B0A3C4] text-[13.5px] leading-relaxed mt-3">{p.description}</p>
                                    <div className="mt-5 inline-flex items-center gap-1.5 text-[#E9CCFF] text-sm font-display font-medium">
                                        Explore product <ArrowRight className="w-3.5 h-3.5" />
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    );
                })}
            </div>
        </section>
    </div>
);

export default Products;
